import React from 'react';

import { Trophy, Star, BookOpen } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import type { LeaderboardEntry } from './leaderboardData';

const tierLabel: Record<string, string> = {
  new: 'New',
  trusted: 'Trusted',
  historian: 'Historian',
  elder: 'Elder',
};

const tierColors: Record<string, string> = {
  new: 'var(--muted-foreground)',
  trusted: '#008080',
  historian: '#E05A47',
  elder: '#F39C12',
};

const podiumConfig = [
  { rank: 2, height: 'h-24', bg: '#5A5A5A18', border: '#5A5A5A33', color: '#5A5A5A', trophy: '🥈' },
  { rank: 1, height: 'h-32', bg: '#F39C1218', border: '#F39C1233', color: '#A06800', trophy: '🥇' },
  { rank: 3, height: 'h-16', bg: '#E05A4714', border: '#E05A4733', color: '#E05A47', trophy: '🥉' },
];

interface PodiumSectionProps {
  top3: LeaderboardEntry[];
}

export default function PodiumSection({ top3 }: PodiumSectionProps) {
  const orderedForDisplay = [top3[1], top3[0], top3[2]].filter(Boolean);

  return (
    <div
      className="rounded-2xl border p-6 mb-8"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
    >
      <div className="flex items-center gap-2 mb-6">
        <Trophy size={16} style={{ color: 'var(--primary)' }} />
        <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
          Top Contributors
        </h2>
      </div>

      <div className="flex items-end justify-center gap-4">
        {orderedForDisplay.map((entry, displayIdx) => {
          const config = podiumConfig[displayIdx];
          if (!entry || !config) return null;

          return (
            <div key={entry.id} className="flex flex-col items-center gap-3 flex-1 max-w-[200px]">
              {/* Card */}
              <div
                className="w-full rounded-xl border p-4 text-center transition-all duration-200 card-hover"
                style={{ backgroundColor: config.bg, borderColor: config.border }}
              >
                <div className="text-2xl mb-2">{config.trophy}</div>
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center text-lg font-bold mx-auto mb-2"
                  style={{
                    backgroundColor: tierColors[entry.tier] + '33',
                    color: tierColors[entry.tier],
                  }}
                >
                  {entry.displayName[0]}
                </div>
                <p className="text-sm font-semibold mb-0.5 truncate" style={{ color: 'var(--foreground)' }}>
                  {entry.displayName}
                </p>
                <p
                  className="text-xs mb-2 truncate"
                  style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', fontSize: '0.65rem' }}
                >
                  @{entry.username}
                </p>
                <Badge
                  variant={`tier-${entry.tier}` as 'tier-new' | 'tier-trusted' | 'tier-historian' | 'tier-elder'}
                  label={tierLabel[entry.tier]}
                />
                <div className="flex items-center justify-center gap-1.5 mt-3">
                  <Star size={12} style={{ color: config.color }} />
                  <span
                    className="text-base font-bold tabular-nums"
                    style={{ color: config.color }}
                  >
                    {entry.reputationScore}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-1.5 mt-1">
                  <BookOpen size={11} style={{ color: 'var(--muted-foreground)' }} />
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    {entry.publishedCount} published
                  </span>
                </div>
              </div>

              {/* Podium block */}
              <div
                className={`w-full ${config.height} rounded-t-lg flex items-center justify-center font-bold text-xl`}
                style={{ backgroundColor: config.border, color: config.color }}
              >
                {config.rank}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}